import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Divider,
  useMediaQuery,
  useTheme,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import SwipeLeftIcon from "@mui/icons-material/SwipeLeft";
import { useNavigate } from "react-router-dom";
import API from "../API/API";
import { BASE_URL } from "../API/Constants";
import { getImageUrl } from "../utils/imageHelper";
import { toast } from "react-toastify";
import dayjs from "dayjs";

const api = new API();

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2', contrastText: '#ffffff' },
    secondary: { main: '#424242', contrastText: '#ffffff' },
    error: { main: '#d32f2f' },
    warning: { main: '#ed6c02' },
    info: { main: '#0288d1' },
    success: { main: '#2e7d32' },
    background: { default: '#f8f9fa', paper: '#ffffff' },
    text: { primary: '#212529', secondary: '#6c757d' }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', borderRadius: 6, fontWeight: 600 },
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 8, backgroundImage: 'none' }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 4, fontWeight: 600 }
      }
    }
  }
});

export default function SavedSubmissions() {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [dataList, setDataList] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDate, setSelectedDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [openImage, setOpenImage] = useState(null);

  useEffect(() => {
    fetchData(selectedDate);
  }, [selectedDate]);

  const fetchData = async (dateParam = selectedDate) => {
    setFetching(true);
    try {
      const params = dateParam ? { date: dateParam } : {};
      const response = await api.get("api/guest-images", params);
      if (response && response.data) {
        const responseData = response.data;
        if (Array.isArray(responseData)) {
          setDataList(responseData);
        } else if (responseData.data && Array.isArray(responseData.data)) {
          setDataList(responseData.data);
        } else if (responseData.guest_images && Array.isArray(responseData.guest_images)) {
          setDataList(responseData.guest_images);
        }
      }
    } catch (error) {
      console.error("Error fetching guest images:", error);
    } finally {
      setFetching(false);
    }
  };

  const handleEdit = (item) => {
    navigate("/guest-images", { state: { editItem: item } });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;

    try {
      const response = await api.delete(`api/guest-images/${id}`);
      if (response) {
        toast.success("Record deleted successfully!");
        fetchData();
      }
    } catch (error) {
      console.error("Error deleting record:", error);
    }
  };



  const renderImages = (itemImages) => {
    if (!itemImages || itemImages.length === 0) return <Typography variant="caption" color="textSecondary">No images</Typography>;

    return (
      <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'nowrap', alignItems: 'center' }}>
        {itemImages.map((img, i) => (
          <Box
            key={i}
            sx={{ cursor: 'pointer', flexShrink: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              setOpenImage(getImageUrl(img));
            }}
          >
            <Box
              component="img"
              src={getImageUrl(img)}
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                objectFit: 'cover',
                border: '1px solid #e0e0e0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'scale(1.08)' }
              }}
              onError={(e) => { e.target.src = 'https://via.placeholder.com/60?text=Error'; }}
            />
          </Box>
        ))}
      </Box>
    );
  };

  const getStatusChipStyle = (statusStr) => {
    const status = (statusStr || '').toLowerCase().replace(/[^a-z]/g, '');
    if (status.includes('checkin') || status.includes('checkedin') || status.includes('completed') || status.includes('approved')) {
      return {
        bgcolor: '#d32f2f', // Red background for CHECKED-IN
        color: '#ffffff',
        fontWeight: 700,
      };
    }
    if (status.includes('waiting') || status.includes('pending')) {
      return {
        bgcolor: '#1976d2', // Blue background for WAITING
        color: '#ffffff',
        fontWeight: 700,
      };
    }
    return {
      bgcolor: '#757575',
      color: '#ffffff',
      fontWeight: 700,
    };
  };

  const isWaitingStatus = (statusStr) => {
    const status = (statusStr || '').toLowerCase().replace(/[^a-z]/g, '');
    return status.includes('waiting') || status.includes('pending') || status === '';
  };

  const filteredData = dataList.filter((item) => {
    const query = searchQuery.toLowerCase();
    const formattedDate = item.created_at ? dayjs(item.created_at).format("DD-MM-YYYY hh:mm A").toLowerCase() : "";
    const isWaiting = isWaitingStatus(item.status);
    return (
      (item.phone_number && item.phone_number.toString().toLowerCase().includes(query)) ||
      (item.room_number && item.room_number.toString().toLowerCase().includes(query)) ||
      (!isWaiting && item.guest_name && item.guest_name.toLowerCase().includes(query)) ||
      (!isWaiting && item.company_name && item.company_name.toLowerCase().includes(query)) ||
      (!isWaiting && item.booking_id && item.booking_id.toString().toLowerCase().includes(query)) ||
      (item.status && item.status.toLowerCase().includes(query)) ||
      (item.username && item.username.toLowerCase().includes(query)) ||
      (item.user_name && item.user_name.toLowerCase().includes(query)) ||
      formattedDate.includes(query)
    );
  });

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ width: '100%', mt: { xs: 0.5, md: 2 }, mb: 3, px: { xs: 1, sm: 2 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1.5, md: 2.5 }, flexWrap: 'wrap', gap: 1 }}>
          <Typography
            sx={{
              fontWeight: 700,
              color: '#1565c0',
              fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
              letterSpacing: 0.3
            }}
          >
            Saved Submissions
          </Typography>

          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate("/guest-images")}
            sx={{
              fontSize: isMobile ? '0.78rem' : '0.875rem',
              py: isMobile ? 0.6 : 0.8,
              px: isMobile ? 1.5 : 2,
              borderRadius: 1.5,
              fontWeight: 600,
              boxShadow: 1
            }}
          >
            Add New Entry
          </Button>
        </Box>

        {/* Outer Card */}
        <Paper elevation={0} sx={{ p: { xs: 1.5, md: 2.5 }, minHeight: 400, border: '1px solid #d0d7de', borderRadius: 2 }}>
          {/* Controls: Search, Date Picker & Refresh */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
              <TextField
                size="small"
                placeholder="Search phone, room, guest, company, booking..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  style: { fontSize: isMobile ? '0.8rem' : '0.875rem' }
                }}
                sx={{ width: { xs: '100%', sm: 280 } }}
              />

              <TextField
                type="date"
                size="small"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  style: { fontSize: isMobile ? '0.8rem' : '0.875rem' }
                }}
                sx={{ width: { xs: '100%', sm: 160 } }}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isMobile && (
                <Chip
                  icon={<SwipeLeftIcon sx={{ fontSize: '0.9rem !important', color: '#1565c0 !important' }} />}
                  label="Scroll right 👉"
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.68rem', color: '#1565c0', borderColor: '#bbdefb', bgcolor: '#e3f2fd', fontWeight: 600 }}
                />
              )}
              <Button
                onClick={() => fetchData(selectedDate)}
                size="small"
                variant="outlined"
                startIcon={<RefreshIcon sx={{ fontSize: '0.9rem !important' }} />}
                sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.5, px: 1.2, borderRadius: 1.5 }}
              >
                Refresh List
              </Button>
            </Box>
          </Box>

          <Divider sx={{ mb: 1.5 }} />

          {fetching ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={250}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            /* Horizontal Scrollable Table for All Devices */
            <TableContainer
              sx={{
                overflowX: 'auto',
                borderRadius: 2,
                border: '1px solid #e1e4e8',
                WebkitOverflowScrolling: 'touch',
                '&::-webkit-scrollbar': { height: 6 },
                '&::-webkit-scrollbar-thumb': { backgroundColor: '#ccc', borderRadius: 3 }
              }}
            >
              <Table sx={{ minWidth: 800 }} size="small">
                <TableHead sx={{ bgcolor: '#f4f7fb' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, py: 1.2, px: 2, color: '#6b8299', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                      Ch-In Name / Ph No
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, py: 1.2, px: 2, color: '#6b8299', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                      Room No
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, py: 1.2, px: 2, color: '#6b8299', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                      ID Images
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, py: 1.2, px: 2, color: '#6b8299', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                      Company
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, py: 1.2, px: 2, color: '#6b8299', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                      Room Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, py: 1.2, px: 2, color: '#6b8299', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                      Username
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.length > 0 ? (
                    filteredData.map((item, index) => (
                      <TableRow
                        key={index}
                        hover
                        sx={{
                          bgcolor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                          '&:last-child td, &:last-child th': { border: 0 }
                        }}
                      >
                        {/* Ch-In Name / Ph No */}
                        <TableCell sx={{ py: 1.2, px: 2, whiteSpace: 'nowrap' }}>
                          <Typography
                            onClick={() => handleEdit(item)}
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.92rem',
                              color: '#1565c0',
                              cursor: 'pointer',
                              lineHeight: 1.2,
                              '&:hover': { textDecoration: 'underline' }
                            }}
                          >
                            {item.phone_number}
                          </Typography>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#212529', textTransform: 'uppercase', mt: 0.3, lineHeight: 1.2 }}>
                            {isWaitingStatus(item.status) ? '-' : (item.guest_name || '-')}
                          </Typography>
                        </TableCell>

                        {/* Room No */}
                        <TableCell sx={{ py: 1.2, px: 2, whiteSpace: 'nowrap' }}>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '1.5px solid #ef5350',
                              borderRadius: '6px',
                              px: 1.4,
                              py: 0.4,
                              minWidth: 48,
                              bgcolor: '#ffffff',
                              color: '#d32f2f',
                              fontWeight: 800,
                              fontSize: '1.3rem'
                            }}
                          >
                            {item.room_number || '-'}
                          </Box>
                        </TableCell>

                        {/* ID Images */}
                        <TableCell sx={{ py: 1.2, px: 2, whiteSpace: 'nowrap' }}>
                          {renderImages(item.images)}
                        </TableCell>

                        {/* Company */}
                        <TableCell sx={{ py: 1.2, px: 2, whiteSpace: 'nowrap' }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#374151', textTransform: 'uppercase', lineHeight: 1.2 }}>
                            {isWaitingStatus(item.status) ? '-' : (item.company_name || '-')}
                          </Typography>
                          <Typography sx={{ fontSize: '0.78rem', color: '#6b7280', mt: 0.3, lineHeight: 1.2 }}>
                            {isWaitingStatus(item.status) ? '-' : (item.booking_id || '-')}
                          </Typography>
                        </TableCell>

                        {/* Room Status */}
                        <TableCell sx={{ py: 1.2, px: 2, whiteSpace: 'nowrap' }}>
                          <Typography sx={{ fontSize: '0.8rem', color: '#4b5563', lineHeight: 1.2, mb: 0.4 }}>
                            {item.created_at ? dayjs(item.created_at).format("DD-MM-YYYY hh:mm A") : "-"}
                          </Typography>
                          <Chip
                            label={item.status || 'waiting'}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.68rem',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              px: 0.6,
                              borderRadius: 1,
                              ...getStatusChipStyle(item.status)
                            }}
                          />
                        </TableCell>

                        {/* Username */}
                        <TableCell sx={{ py: 1.2, px: 2, whiteSpace: 'nowrap' }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#374151', textTransform: 'uppercase' }}>
                            {item.username || item.user_name || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.85rem' }}>No submissions found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Image Preview Modal */}
        <Dialog open={Boolean(openImage)} onClose={() => setOpenImage(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'visible' } }}>
          <IconButton onClick={() => setOpenImage(null)} sx={{ position: 'absolute', right: -12, top: -12, bgcolor: 'white', boxShadow: 2, '&:hover': { bgcolor: '#f5f5f5' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
          <DialogContent sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
            {openImage && <Box component="img" src={openImage} sx={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 1, objectFit: 'contain' }} />}
          </DialogContent>
          <Box sx={{ px: 2, pb: 2, textAlign: 'center' }}>
            <Button variant="contained" onClick={() => setOpenImage(null)} sx={{ borderRadius: 1.5, textTransform: 'none', px: 3, py: 0.6, fontSize: '0.85rem' }}>Close</Button>
          </Box>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}
